import { useMemo } from 'react';
import type { Currency, CurrencyHistory, DailyExchangeRate } from '@/types';
import { currencyApi } from '@/lib/api';
import { useOptimizedQuery } from './useOptimizedQuery';

/**
 * Get the last 30 days of real exchange rate history from frankfurter.dev
 * Powered by European Central Bank data
 */
export function useCurrencyHistory(
  baseCurrency: Currency = 'ISK',
  targetCurrency: Currency = 'EUR'
) {
  // Fetch historical rates from frankfurter.dev API
  const ratesQuery = useOptimizedQuery({
    queryKey: ['currency-history', baseCurrency, targetCurrency],
    queryFn: async () => {
      try {
        // Get historical rates for last 30 days
        const data = await currencyApi.getHistoricalRates(baseCurrency, targetCurrency);

        // Transform API response to DailyExchangeRate array
        const history: DailyExchangeRate[] = Object.entries(data.rates || {})
          .map(([date, rates]) => ({
            date,
            rate: parseFloat(((rates as Record<string, number>)[targetCurrency] || 0).toFixed(4)),
          }))
          .sort((a, b) => a.date.localeCompare(b.date)); // Sort oldest to newest

        return {
          baseCurrency,
          targetCurrency,
          history,
          currentRate: history.length > 0 ? history[history.length - 1].rate : 0,
        };
      } catch (error) {
        console.error('Failed to fetch currency history:', error);
        throw error;
      }
    },
    tier: 'realtime',
  });

  // Calculate statistics from history
  const processedHistory = useMemo(() => {
    if (!ratesQuery.data) return null;

    const { history, baseCurrency: base, targetCurrency: target } = ratesQuery.data;
    // History is already sorted oldest to newest from API (perfect for chart display)
    const rates = history.map((h) => h.rate);

    return {
      baseCurrency: base,
      targetCurrency: target,
      history,
      minRate: Math.min(...rates),
      maxRate: Math.max(...rates),
      averageRate: rates.reduce((a, b) => a + b, 0) / rates.length,
    } as CurrencyHistory;
  }, [ratesQuery.data]);

  return {
    data: processedHistory,
    isLoading: ratesQuery.isLoading,
    isError: ratesQuery.isError,
    error: ratesQuery.error,
  };
}

/**
 * Calculate percentage change between two rates
 */
export function calculatePercentageChange(oldRate: number, newRate: number): number {
  if (oldRate === 0) return 0;
  return ((newRate - oldRate) / oldRate) * 100;
}

/**
 * Format rate with appropriate decimal places
 */
export function formatRate(rate: number, decimals: number = 4): string {
  return rate.toFixed(decimals);
}
